import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "reactstrap";
import { createSelector } from "reselect";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import type { RootState } from "store";
import { fetchOrderHistoryThunk } from "slices/paygcc/thunk";
import { OrderHistoryRequestPayload } from "services/paygccService";
import { loginSuccess } from "slices/auth/login/reducer";
import { toast } from "react-toastify";
import DashboardHeader from "../../Components/Common/DashboardHeader";

const selectLoginState = (state: any) => state.Login;
const loginSelector = createSelector(selectLoginState, (login: any) => ({
  isAuthenticated: login.isAuthenticated,
}));

const OrderHistory = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const { isAuthenticated } = useSelector(loginSelector);
  const { orderHistory, orderHistoryPagination, orderHistoryLoading, orderHistoryError } =
    useSelector((state: RootState) => (state as any).paygcc);

  const [searchKey, setSearchKey] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  
  useEffect(() => {
    const authUser = sessionStorage.getItem("authUser");
    if (authUser && !isAuthenticated) {
      dispatch(loginSuccess());
    }
  }, [dispatch, isAuthenticated]);

  const [filters, setFilters] = useState<OrderHistoryRequestPayload>({
    page: 1,
    per_page: 10,
    gatwayname: "",
    servicename: "",
    product_name: "",
    from_date: "",
    to_date: "",
    searchkey: "",
    status: "",
  });

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    const payload: OrderHistoryRequestPayload = {
      ...filters,
      page: currentPage,
      searchkey: searchKey,
    };
    dispatch(fetchOrderHistoryThunk(payload));
  }, [dispatch, currentPage, searchKey, isAuthenticated, filters]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKey(e.target.value);
    setCurrentPage(1);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusClass = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === "success" || statusLower === "completed") {
      return "status-completed";
    }
    if (statusLower === "in-progress" || statusLower === "inprogress") {
      return "status-in-progress";
    }
    if (statusLower === "pending") {
      return "status-pending";
    }
    return "status-default";
  };

  const handleDownloadInvoice = (order: any) => {
    const statusLower = order.status?.toLowerCase();
    
    if (statusLower === "pending") {
      toast.info("Payment under process", {
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
      });
      return;
    }

    if (order.invoice_url) {
      if (order.invoice_url.includes("localhost") || order.invoice_url.includes("127.0.0.1")) {
        toast.info("Payment under process", {
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: false,
        });
        return;
      }
      window.open(order.invoice_url, '_blank');
    } else {
      toast.error("Invoice is not available for this order.", {
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: false,
      });
    }
  };


  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const totalPages = orderHistoryPagination?.total_pages || 1;

  document.title = "Order History - Amanat";

  return (
    <div className="order-history-page">
      <DashboardHeader />

      <div className="breadcrumbs">
        <div className="container">
          <a
            href="https://amanatfintech.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Home
          </a>
          <span className="separator"> &gt; </span>
          <span>Report</span>
          <span className="separator"> &gt; </span>
          <span className="current-page">Order History</span>
        </div>
      </div>

      <main className="order-history-main">
        <div className="container">

          <div className="search-filter-section">
            <div className="search-bar">
              <i className="ri-search-line search-icon"></i>
              <input
                type="text"
                placeholder="Search by name.."
                value={searchKey}
                onChange={handleSearch}
                className="search-input"
              />
            </div>
          </div>

          {!isAuthenticated ? (
            <div className="error-state">Please login to view your order history.</div>
          ) : orderHistoryLoading ? (
            <div className="loading-state">Loading orders...</div>
          ) : orderHistoryError ? (
            <div className="error-state">
              <p>Error: {orderHistoryError}</p>
              <Button 
                color="primary" 
                onClick={() => {
                  const payload: OrderHistoryRequestPayload = {
                    ...filters,
                    page: currentPage,
                    searchkey: searchKey,
                  };
                  dispatch(fetchOrderHistoryThunk(payload));
                }}
              >
                Retry
              </Button>
            </div>
          ) : orderHistory && orderHistory.length > 0 ? (
            <div className="order-cards">
              {orderHistory.map((order: any) => (
                <div key={order.id} className="order-card">
                  <div className="order-card-header">
                    <span className={`status-badge ${getStatusClass(order.status)}`}>
                      {order.status}
                    </span>
                  </div>
                  <div className="order-card-body">
                    <div className="order-image-section">
                      <div className="order-image">
                        <img
                          src={order.thumbnail_url || "https://via.placeholder.com/100x100?text=Course"}
                          alt={order.product_name || "Course"}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "https://via.placeholder.com/100x100?text=Course";
                          }}
                        />
                      </div>
                      <div className="order-id">#{order.paymentno || `Ord ID-${order.id}`}</div>
                    </div>
                    <div className="order-details">
                      <h3 className="order-title">{order.product_name || "Course"}</h3>
                      <div className="order-meta">
                        <span className="order-date">
                          <i className="ri-calendar-line"></i>
                          {formatDate(order.createdon)}
                        </span>
                        <span className="order-price">BD {order.total?.toFixed(2) || "0.00"}</span>
                      </div>
                    </div>
                    <div className="order-actions">
                      <Button
                        className="download-invoice-btn"
                        outline
                        onClick={() => handleDownloadInvoice(order)}
                      >
                        Download Invoice
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-orders">
              <p>No orders found.</p>
              {!orderHistoryLoading && (
                <Button 
                  color="primary" 
                  outline
                  onClick={() => {
                    const payload: OrderHistoryRequestPayload = {
                      ...filters,
                      page: 1,
                      searchkey: "",
                    };
                    setSearchKey("");
                    setCurrentPage(1);
                    dispatch(fetchOrderHistoryThunk(payload));
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          )}

          {!orderHistoryLoading &&
            orderHistory &&
            orderHistory.length > 0 &&
            totalPages > 1 && (
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                <div className="pagination-info">
                  Page {currentPage} of {totalPages}
                </div>
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  aria-label="Next page"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
        </div>
      </main>
    </div>
  );
};

export default OrderHistory;
